import Immutable from 'immutable';

import type ErrorData from 'sharedreact/data/ErrorData';
import Filter from 'sharedreact/data/Filter';
import GroupAndCount from 'sharedreact/data/GroupAndCount';
import SortOrder from 'sharedreact/data/SortOrder';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import type { RangeType } from 'sharedreact/stores/generic/Type';

import TokensConnection from 'workspaces/launcher/connections/TokensConnection';
import TokensConstants from 'workspaces/launcher/constants/TokensConstants';
import Token from 'workspaces/launcher/data/Token';

function _fetchSuccess (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<Token>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder
) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.GET_SUCCESS,
    range: range,
    totalCount: totalCount,
    items: items,
    countPerGroup: countPerGroup,
    filter: filter,
    advancedFilter: advancedFilter,
    groupBy: groupBy,
    sortOrder: sortOrder,
    logLevel: 'debug'
  });
}

function _fetchFailure (error: ErrorData) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.GET_FAIL,
    error: error,
    logLevel: 'trace'
  });
}

function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  groupBy: SortOrder,
  sortOrder: SortOrder
) {
  TokensConnection.fetch(
    range,
    filter,
    advancedFilter,
    groupBy,
    sortOrder,
    _fetchSuccess,
    _fetchFailure
  );
}

function setNeedsConfirm (needsConfirm: boolean) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.NEEDS_CONFIRM,
    needsConfirm: needsConfirm,
    logLevel: 'trace'
  });
}

function _createSuccess (
  status: string,
  tokenString: string
) {
  if (status === 'ok') {
    AppDispatcher.dispatch({
      actionType: TokensConstants.CREATE_SUCCESS,
      tokenString: tokenString,
      logLevel: 'trace'
    });
  } else if (status === 'needsConfirm') {
    setNeedsConfirm(true);
  }
}

function _createFailure (error: ErrorData) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.CREATE_FAIL,
    error: error,
    logLevel: 'trace'
  });
}

function _removeSuccess (ids: Immutable.List<string>) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.REMOVE_SUCCESS,
    ids: ids,
    logLevel: 'trace'
  });
}

function _removeFailure (error: ErrorData) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.REMOVE_FAIL,
    error: error,
    logLevel: 'trace'
  });
}

function _updateSuccess (nrTags: string) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.UPDATE_SUCCESS,
    nrTags: nrTags,
    logLevel: 'trace'
  });
}

function _updateFailure (error: ErrorData) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.UPDATE_FAIL,
    error: error,
    logLevel: 'trace'
  });
}

function create (userId: string, tags: string, force: boolean) {
  TokensConnection.generateNewToken(userId, tags, force, _createSuccess, _createFailure);
}

function hideClipboardMsg () {
  AppDispatcher.dispatch({
    actionType: TokensConstants.HIDE_CLIPBOARDMSG,
    logLevel: 'trace'
  });
}

function closeDetails () {
  AppDispatcher.dispatch({
    actionType: TokensConstants.CLOSE_DETAILS,
    logLevel: 'trace'
  });
}

function remove (identifiers: Immutable.List<string>) {
  TokensConnection.revokeTokens(identifiers, _removeSuccess, _removeFailure);
}

function update (userId: string, tags: string) {
  TokensConnection.updateToken(userId, tags, _updateSuccess, _updateFailure);
}

function setVisibleRange (range: RangeType) {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SET_VISIBLE_RANGE,
    range: range,
    logLevel: 'trace'
  });
}

function setFilter (filterMode: string, filter: Filter, filterString: string): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SET_FILTER,
    filterMode: filterMode,
    filter: filter,
    filterString: filterString,
    logLevel: 'trace'
  });
}

function activateFilter (filterMode: string): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.ACTIVATE_FILTER,
    filterMode: filterMode,
    logLevel: 'trace'
  });
}

function setSortOrder (sortOrder: SortOrder): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SET_SORTORDER,
    sortOrder: sortOrder,
    logLevel: 'trace'
  });
}

function showCopyButton (): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SHOW_COPY_BUTTON,
    show: true,
    logLevel: 'trace'
  });
}

function hideCopyButton (): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SHOW_COPY_BUTTON,
    show: false,
    logLevel: 'trace'
  });
}

function showClipboardMsg (): void {
  AppDispatcher.dispatch({
    actionType: TokensConstants.SHOW_CLIPBOARDMSG,
    logLevel: 'trace'
  });
}

const TokensActions = {
  fetch,
  create,
  showCopyButton,
  hideCopyButton,
  showClipboardMsg,
  hideClipboardMsg,
  remove,
  update,
  setVisibleRange,
  setNeedsConfirm,
  setFilter,
  activateFilter,
  setSortOrder,
  closeDetails
};

export default TokensActions;
